import { useAuthStore } from '~/stores/auth'
import { useUserStore } from '~/stores/user'
import { decideAccessAction } from '~/utils/accessGate'

/**
 * Access gate — the single home for every "you don't belong here → 403"
 * verdict, covering both organization and workspace membership.
 *
 * - Not signed in → continue (auth middleware owns that redirect)
 * - No `organizationSlug` param → continue. Public/top-level routes (`/simulation/:id`,
 *   `/test/:id`, `/lti/…`) carry no organization param, so they are exempt by
 *   construction — no allow-list to keep in sync. Rests on the invariant that
 *   no public/unauthenticated route is organization/workspace scoped; a future
 *   organization-scoped public route must handle its own exemption here.
 * - Workspace page (`/:organizationSlug/:wsid/…`) not a member workspace → /403
 * - Organization page (`/:organizationSlug`, `/:organizationSlug/settings`) not a member organization → /403
 *
 * The verdict is a 403, not a 404: telling the user "you don't have access —
 * ask an admin" (matching the old staging behaviour, where the workspace store
 * raised `showError({ statusCode: 403 })`) was judged more useful than hiding
 * whether the resource exists. The gate never says *which* org/workspace check
 * failed, and workspace/organization ids are unguessable UUIDs/slugs, so the
 * enumeration surface this opens is minimal.
 *
 * Redirects to `/403` (mirrors `02.feat`'s `/404`) rather than `showError()`:
 * in SPA mode (`ssr: false`) an error set from middleware is cleared on
 * client-side navigation, so `showError` would silently fail to gate a
 * client-side nav. `/403` is a static page, so it resolves ahead of the
 * `[organizationSlug]` route (no loop) and the gate early-exits on it (no
 * `organizationSlug` param).
 *
 * Runs after `04.organization-slug`, so params are already canonical.
 */
export default defineNuxtRouteMiddleware(async (to) => {
  const organizationSlug = to.params.organizationSlug

  // Early exit — no organization param → not an organization/workspace route → the gate can never
  // apply; skip loading the user store (mirrors 03/04).
  if (!organizationSlug || typeof organizationSlug !== 'string') return

  const authStore = useAuthStore()

  // Not signed in → auth middleware owns the redirect.
  if (!authStore.isAuthenticated) return

  const userStore = useUserStore()

  try {
    await userStore.$ensureInitialised()
  } catch {
    // Can't load membership → fail open, same policy as onboarding/organization-slug:
    // better a stale URL than a blocked navigation.
    return
  }

  const workspaceId =
    typeof to.params.workspaceId === 'string'
      ? to.params.workspaceId
      : undefined

  // Super-users are NOT a blanket bypass: they must join a cross-org workspace
  // (`system admin > workspaces > join workspace`) or organisation
  // (`system admin > organisations > join organisation`) — each creates the
  // membership row — before the gate lets them in. The verdict logic lives in
  // the pure `decideAccessAction`; here we resolve the membership snapshot.
  const decide = () =>
    decideAccessAction({
      organizationSlug,
      workspaceId,
      isUserInWorkspace: workspaceId
        ? userStore.getIsUserInWorkspace(workspaceId)
        : false,
      isOrganizationMember: userStore.organizations.some(
        (organization) => organization.slug === organizationSlug,
      ),
    })

  // Membership snapshot can lag reality by up to the store's 2-minute refresh
  // poll — a user joined to a workspace mid-session would be rejected on a link
  // they legitimately hold. Before rejecting, refresh the snapshot once and
  // re-decide; a refresh failure leaves the stale snapshot in place and the
  // verdict stands.
  let decision = decide()
  if (decision.type === 'redirect') {
    try {
      await userStore.$refresh()
      decision = decide()
    } catch {
      // Refresh failed → stale snapshot stands, verdict stands.
    }
  }

  if (decision.type === 'redirect') return navigateTo(decision.path)

  // Member organization/workspace → continue.
})
